import { Box, SegmentedToggle } from '@wix/design-system';
import { CodeSmall, LinkSmall } from '@wix/wix-ui-icons-common';
import React, { useContext, useState } from 'react';

import { RicosContext } from './../../../ricos-context';
import type { PmHtmlNode } from './../../../_shared/pm-node-types';
import { LinkInput } from './../../../ui-components';

import { EDIT_PANEL_SEGMENTED_TOGGLE, EDIT_PANEL_URL_INPUT } from '../../data-hooks';
import { HtmlInputArea } from './HtmlInputArea';

export type HtmlSourceInputProps = {
  htmlNodeAttrs?: Partial<PmHtmlNode['attrs']>;
  onHtmlInputChange?: (html: string) => void;
  onLinkInputChange?: (url: string) => void;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
};

const INPUT_HTML = 'html';
const INPUT_URL = 'url';
type InputType = typeof INPUT_HTML | typeof INPUT_URL;

export const HtmlSourceInput: React.FC<HtmlSourceInputProps> = ({
  htmlNodeAttrs,
  onHtmlInputChange,
  onLinkInputChange,
  inputRef,
}) => {
  const { t } = useContext(RicosContext);
  const [inputType, setInputType] = useState<InputType>(
    htmlNodeAttrs?.url ? INPUT_URL : INPUT_HTML
  );

  const onInputToggleClick = (_: React.SyntheticEvent, value: string) => {
    inputType !== value && setInputType(value as InputType);
  };

  return (
    <Box direction="vertical" gap="SP3">
      <SegmentedToggle
        defaultSelected={inputType}
        size="small"
        onClick={onInputToggleClick}
        dataHook={EDIT_PANEL_SEGMENTED_TOGGLE}
      >
        <SegmentedToggle.Button value={INPUT_HTML} prefixIcon={<CodeSmall />}>
          {t('HtmlEditPanel_Code')}
        </SegmentedToggle.Button>
        <SegmentedToggle.Button value={INPUT_URL} prefixIcon={<LinkSmall />}>
          {t('HtmlEditPanel_Source')}
        </SegmentedToggle.Button>
      </SegmentedToggle>
      {inputType === INPUT_HTML ? (
        <HtmlInputArea
          placeholder={t('HtmlEditPanel_HtmlInput_Placeholder')}
          value={htmlNodeAttrs?.html ?? ''}
          onChange={(value: string) => {
            onHtmlInputChange?.(value);
          }}
        />
      ) : (
        <LinkInput
          value={htmlNodeAttrs?.url ?? ''}
          placeholder={t('HtmlEditPanel_UrlInput_Placeholder')}
          size="small"
          labelEnabled={false}
          dataHook={EDIT_PANEL_URL_INPUT}
          inputRef={inputRef}
          onChange={url => {
            onLinkInputChange?.(url ?? '');
          }}
        />
      )}
    </Box>
  );
};
